/**
 * Lazy load avec retry en cas de ChunkLoadError (réseau instable, hot reload, etc.)
 * Évite l'écran blanc quand un chunk ne se charge pas.
 */
import React, { ComponentType, lazy } from 'react';

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

function isChunkLoadError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('ChunkLoadError'))
  );
}

async function importWithRetry<T>(
  importFn: () => Promise<{ default: T }>,
  retriesLeft: number
): Promise<{ default: T }> {
  try {
    return await importFn();
  } catch (error) {
    if (isChunkLoadError(error) && retriesLeft > 0) {
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
      return importWithRetry(importFn, retriesLeft - 1);
    }
    throw error;
  }
}

export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(() => importWithRetry(importFn, MAX_RETRIES));
}

/** Fallback affiché quand un chunk échoue après les retries (bouton Rafraîchir) */
export const ChunkLoadErrorFallback: React.FC<{ onRetry?: () => void }> = ({ onRetry }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 200,
      padding: 24,
      textAlign: 'center',
    }}
  >
    <p style={{ marginBottom: 16 }}>
      Le chargement de cette page a échoué (connexion instable ou mise à jour).
    </p>
    <button
      type="button"
      onClick={() => (onRetry ? onRetry() : window.location.reload())}
      style={{
        padding: '8px 16px',
        cursor: 'pointer',
        backgroundColor: '#E91E63',
        color: '#fff',
        border: 'none',
        borderRadius: 6,
      }}
    >
      Rafraîchir la page
    </button>
  </div>
);
